import { useNavigate } from "react-router-dom";
import Avatar from "../common/Avatar";
import useTranslate from "../../hooks/useTranslate";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { resolveCountry, Flag } from "./lawyerFlag";
import {
  CheckmarkIcon,
  ChatBubbleIcon,
  EnvelopeIcon,
} from "./v2/icons";

// ============================================================================
// LawyerCard — presentation rebuilt 1:1 from the Lawyers V2 handoff (SHARED
// LawyerCard spec, boards 1a/1b/1e desktop + 1k mobile). All logic, props and
// handler wiring are byte-identical to the previous build — this is a
// presentation-layer rebuild only (brief rules 1/6/7).
//
// Desktop (md+): padding 18/18/16, gap 11, avatar 46, badge row, countries +
//   langs row, spec chips, footer (border-top #EEF1F6) with CTA then opt-in
//   contact rows (artboard order: button above contact rows).
// Mobile (<md, board 1k): padding 16, gap 10, avatar 44, NO spec chips and NO
//   contact rows on the card (they live on the mobile detail, board 1m),
//   button radius 10 / 11px pad / 13.5px.
// CTA triad (Contact / Request pending / Edit profile) kept — only "Contact
// Lawyer" is drawn on the boards; the other two reuse its exact geometry with
// the handoff's secondary/disabled colors (parity file, section G).
// ============================================================================

const asList = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);

// Dual-role = user holds the Agent role in addition to Lawyer. The backend may
// expose this as an explicit `is_dual` flag or a `roles` list; support both.
const isDualRole = (lawyer) => {
  if (lawyer.is_dual === true) return true;
  return asList(lawyer.roles).some((r) => String(r).toLowerCase() === "agent");
};

const LawyerCard = ({ lawyer, onContact, onView, isSelf = false, isPending = false }) => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  // Mobile card variant per board 1k (breakpoint = the platform's md switch,
  // same as the rest of the directory).
  const isMobile = useMediaQuery("(max-width: 767px)");

  // Specializations: prefer the plural array, fall back to the legacy singular.
  const specializations = asList(lawyer.specializations).length
    ? asList(lawyer.specializations)
    : (lawyer.specialization || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

  // Jurisdictions: prefer the multi-value array, fall back to the single
  // country field. Resolve each to a flag + label.
  const jurisdictionValues = asList(lawyer.jurisdictions).length
    ? asList(lawyer.jurisdictions)
    : lawyer.country
    ? [lawyer.country]
    : [];
  const jurisdictions = jurisdictionValues.map(resolveCountry);

  const languages = asList(lawyer.languages);
  const verified = lawyer.is_verified === true;
  const dual = isDualRole(lawyer);

  // Firm line: the design shows "Firm · City". Fall back to HQ (city/country)
  // when no firm is set so the line still reads well.
  const firmLine =
    lawyer.firm ||
    [lawyer.city, lawyer.country].filter(Boolean).join(", ") ||
    "";

  const handleContact = (e) => {
    e.stopPropagation();
    if (onContact) onContact(lawyer);
  };

  const handleView = () => {
    if (onView) onView(lawyer);
  };

  const handleEditProfile = (e) => {
    e.stopPropagation();
    navigate("/settings/lawyer-profile");
  };

  return (
    <div
      onClick={handleView}
      className={`bg-white rounded-[14px] border border-[#E6EAF2] shadow-[0_1px_2px_rgba(16,24,40,0.05)] p-4 md:pt-[18px] md:px-[18px] md:pb-4 flex flex-col gap-2.5 md:gap-[11px] h-full ${
        onView ? "cursor-pointer hover:border-[#C9D3E3] transition-colors" : ""
      }`}
    >
      {/* Identity row: avatar 46 (44 mobile) + name + firm line */}
      <div className="flex items-center gap-3">
        <Avatar
          src={lawyer.profile_picture_url}
          alt={lawyer.name}
          fallbackText={lawyer.name}
          size={isMobile ? 44 : 46}
          className="flex-none border border-[#E1E6EF]"
          initialsSize={isMobile ? 14 : 15}
        />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-[14.5px] md:text-[15px] text-[#101828] leading-[1.3] truncate">
            {lawyer.name}
          </h3>
          {firmLine && (
            <p className="text-[12px] md:text-[12.5px] text-[#667085] mt-[1px] md:mt-0.5 whitespace-nowrap overflow-hidden text-ellipsis">
              {firmLine}
            </p>
          )}
        </div>
      </div>

      {/* Badge row: verified + dual-role (each only when applicable) */}
      {(verified || dual) && (
        <div className="flex flex-wrap gap-1.5">
          {verified && (
            <span className="inline-flex items-center gap-1 px-[9px] py-[3px] rounded-full bg-[#EBF3FF] text-[#1567D2] text-[11px] md:text-[11.5px] font-semibold border border-[#C9E1FF]">
              <CheckmarkIcon size={isMobile ? 10 : 11} strokeWidth={3} />
              {t("Verified Lawyer")}
            </span>
          )}
          {dual && (
            <span className="inline-flex items-center px-[9px] py-[3px] rounded-full bg-[#F4F1FE] text-[#6941C6] text-[11px] md:text-[11.5px] font-semibold border border-[#E4DBFB]">
              {t("Agent & Lawyer")}
            </span>
          )}
        </div>
      )}

      {/* Countries + languages row (langs right-aligned, artboard style) */}
      {(jurisdictions.length > 0 || languages.length > 0) && (
        <div className="flex items-center gap-2.5 md:gap-3 flex-wrap text-[12px] md:text-[12.5px] text-[#344054] font-medium">
          {jurisdictions.map((j, i) => (
            <span
              key={`${j.label}-${i}`}
              className="inline-flex items-center gap-1 md:gap-[5px]"
            >
              <Flag {...j} width={18} height={13} />
              {j.label}
            </span>
          ))}
          {languages.length > 0 && (
            <span className="ml-auto min-w-0 truncate text-[#98A2B3] text-[10.5px] md:text-[11px] font-semibold tracking-[0.4px]">
              {languages.join(" · ")}
            </span>
          )}
        </div>
      )}

      {/* Specialization chips row — desktop only (board 1k mobile card omits it) */}
      {specializations.length > 0 && (
        <div className="hidden md:flex flex-wrap gap-1.5">
          {specializations.map((spec, i) => (
            <span
              key={`${spec}-${i}`}
              className="px-[9px] py-[3px] rounded-full bg-[#F7F8FA] text-[#475467] text-[11.5px] font-medium border border-[#E7EBF0]"
            >
              {spec}
            </span>
          ))}
        </div>
      )}

      {/* Footer: CTA then opt-in contact rows (artboard order). `mt-auto`
          anchors it to the card bottom so cards of unequal height all keep the
          button on the same line (equal-height grid rows). */}
      <div className="mt-auto md:pt-3 md:border-t md:border-[#EEF1F6] flex flex-col gap-[9px]">
        {/* Primary CTA — chat-first. On the viewer's OWN card, self-contact is
            rejected by the backend, so instead offer a quick link to edit their
            lawyer profile. License number lives in the detail view. */}
        {isSelf ? (
          <button
            onClick={handleEditProfile}
            className="w-full flex items-center justify-center rounded-[10px] md:rounded-[9px] py-[11px] md:py-[9px] bg-white border border-[#DDE3EC] text-[#344054] text-[13.5px] md:text-[13px] font-semibold hover:bg-[#F5F7FB] transition-colors"
          >
            {t("Edit profile")}
          </button>
        ) : isPending ? (
          // D-010: a request is already pending with this lawyer.
          <button
            type="button"
            disabled
            onClick={(e) => e.stopPropagation()}
            className="w-full flex items-center justify-center rounded-[10px] md:rounded-[9px] py-[11px] md:py-[9px] border border-[#DDE3EC] bg-[#F7F8FA] text-[#98A2B3] text-[13.5px] md:text-[13px] font-semibold cursor-not-allowed"
          >
            {t("Request pending")}
          </button>
        ) : (
          <button
            onClick={handleContact}
            className="w-full flex items-center justify-center gap-[7px] rounded-[10px] md:rounded-[9px] py-[11px] md:py-[9px] bg-[#2283FF] hover:bg-[#1570E8] text-white text-[13.5px] md:text-[13px] font-semibold transition-colors"
          >
            <ChatBubbleIcon size={14} />
            {t("Contact Lawyer")}
          </button>
        )}

        {/* Contact row — email only when present (privacy opt-in enforced at
            the serializer; FE renders what arrives). Phone is REMOVED from all
            lawyer surfaces (Marin product decision 2026-07-23, overrides
            A7/A18) — even when the API sends `mobile`, it is not rendered.
            Desktop only: board 1k omits the row on the mobile card (email
            shows on mobile detail). The row only renders when the email
            exists, so the card looks complete in both variants (chat-only and
            chat+email). */}
        {lawyer.email && (
          <div className="hidden md:flex gap-3.5 flex-wrap text-[12px] text-[#475467]">
            <span className="inline-flex items-center gap-[5px] min-w-0">
              <EnvelopeIcon size={12} />
              <span className="truncate">{lawyer.email}</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default LawyerCard;
